import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import { 
  getApplicationById, 
  getApplicationParameters,
  updateApplicationParameter
} from '../../services/applications/applicationsService';
import { ApplicationDto, ParameterDto, ParameterType } from '../../services/applications/types';

const ParameterEdit: React.FC = () => {
  const { id, parameterId } = useParams<{ id: string; parameterId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [formData, setFormData] = useState<ParameterDto>({
    applicationId: id,
    type: undefined,
    content: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (id && parameterId) {
      fetchData(id, parameterId);
    }
  }, [id, parameterId, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async (applicationId: string, paramId: string) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to edit parameters.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both application details and parameter data in parallel
      const [appData, parametersData] = await Promise.all([
        getApplicationById(auth.user || null, applicationId),
        getApplicationParameters(auth.user || null, applicationId)
      ]);
      
      setApplication(appData);
      
      // Find the specific parameter to edit
      const parameterToEdit = parametersData.find(param => param.id === paramId);
      
      if (parameterToEdit) {
        setFormData(parameterToEdit);
      } else {
        setError('Parameter not found. Please check the URL and try again.');
      }
    } catch (err) {
      setError('Failed to fetch parameter data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to update a parameter.');
      return;
    }
    
    if (!id || !parameterId) {
      setError('Application ID or Parameter ID is missing.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await updateApplicationParameter(auth.user || null, id, parameterId, formData);
      navigate(`/applications/${id}/parameters`);
    } catch (err) {
      setError('Failed to update parameter. Please try again.');
      console.error('Error updating parameter:', err);
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/applications/${id}/parameters`);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader
        title="Edit Parameter"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: "Parameters", to: `/applications/${id}/parameters` },
          { label: "Edit Parameter" }
        ]}
      />
      
      <Card>
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label 
                htmlFor="type" 
                className="block text-sm font-medium text-gray-700"
              >
                Parameter Type *
              </label>
              <select
                id="type"
                name="type"
                required
                value={formData.type || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Select parameter type</option>
                {Object.entries(ParameterType).map(([key, value]) => (
                  <option key={key} value={value}>
                    {key.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label 
                htmlFor="content" 
                className="block text-sm font-medium text-gray-700"
              >
                Content *
              </label>
              <textarea
                id="content"
                name="content"
                required
                value={formData.content || ''}
                onChange={handleInputChange}
                rows={4}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter parameter content"
              />
              {formData.type && (
                <p className="mt-1 text-sm text-gray-500">
                  {formData.type === ParameterType.INSTRUCTION_TEXT && 
                    "System instructions that guide the AI's behavior."}
                  {formData.type === ParameterType.CHARACTER_LIMIT && 
                    "Maximum number of characters allowed."}
                  {formData.type === ParameterType.APPLICATION_KEY && 
                    "API key or secret for this application."}
                  {(formData.type === ParameterType.TELEGRAM_API_TOKEN || 
                    formData.type === ParameterType.TELEGRAM_BOT_ID) && 
                    "Credentials for Telegram integration."}
                </p>
              )}
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
            >
              Update Parameter
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ParameterEdit;